import {
  IEmailTemplateChild,
  IGenerateEmailTemplateChildrenResponse,
  IRewriteEmailTemplateToneRequest,
  IRewriteEmailTemplateToneResponse,
} from 'shared';

import { CacheTags } from '../../types/cacheTags';
import { orgHeader } from '../../../../common/lib/axiosBaseQuery';
import { emptySplitApi } from '../baseApi';

export const emailTemplatesApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    fetchEmailTemplates: builder.query({
      query: org => ({
        url: `email-templates`,
        method: `GET`,
        headers: {
          [orgHeader]: org,
        },
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformResponse: (response: any) => {
        return response?.templates;
      },
      providesTags: result => result ?
        [
          ...result.flatMap(({ id, children }: { id: string; children: IEmailTemplateChild[] }) => [id, ...children.map(child => child.id)])
            .map(id => ({ type: CacheTags.EmailTemplates, id })),
          { type: CacheTags.EmailTemplates, id: `LIST` },
        ]
        : [{ type: CacheTags.EmailTemplates, id: `LIST` }],
    }),
    createEmailTemplate: builder.mutation({
      query: ({ org, type }) => {
        return ({
          url: `email-templates`,
          method: `POST`,
          params: {
            type,
          },
          headers: {
            [orgHeader]: org,
          },
        });
      },
      invalidatesTags: [{ type: CacheTags.EmailTemplates, id: `LIST` }],
    }),
    updateEmailTemplate: builder.mutation({
      query: ({ org, id, ...update }) => ({
        url: `email-templates/${id}`,
        method: `PATCH`,
        headers: {
          [orgHeader]: org,
        },
        data: update,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: CacheTags.EmailTemplates, id }],
    }),
    deleteEmailTemplate: builder.mutation({
      query: ({ org, id }) => ({
        url: `email-templates/${id}`,
        method: `DELETE`,
        headers: {
          [orgHeader]: org,
        },
      }),
      invalidatesTags: () => [{ type: CacheTags.EmailTemplates, id: `LIST` }],
    }),
    duplicateEmailTemplate: builder.mutation({
      query: ({ org, id }) => ({
        url: `email-templates/duplicate/${id}`,
        method: `POST`,
        headers: {
          [orgHeader]: org,
        },
      }),
      invalidatesTags: () => [{ type: CacheTags.EmailTemplates, id: `LIST` }],
    }),
    sendTestEmail: builder.mutation({
      query: ({ org, templateId, senderProfileId }) => ({
        url: `email-templates/${templateId}/test`,
        method: `POST`,
        headers: {
          [orgHeader]: org,
        },
        data: {
          senderProfileId,
        },
      }),
    }),
    createEmailTemplateChild: builder.mutation<{ id: string }, { org: string; id: string }>({
      query: ({ org, id }) => ({
        url: `email-templates/${id}/children`,
        method: `POST`,
        headers: {
          [orgHeader]: org,
        },
      }),
      invalidatesTags: [{ type: CacheTags.EmailTemplates as const, id: `LIST` }],
    }),
    generateEmailTemplateChildren: builder.mutation<IGenerateEmailTemplateChildrenResponse, { org: string; id: string }>({
      query: ({ org, id }) => ({
        url: `email-templates/${id}/children/generate`,
        method: `POST`,
        headers: {
          [orgHeader]: org,
        },
      }),
      invalidatesTags: [{ type: CacheTags.EmailTemplates as const, id: `LIST` }],
    }),
    rewriteEmailTemplateTone: builder.mutation<IRewriteEmailTemplateToneResponse, { org: string; id: string } & IRewriteEmailTemplateToneRequest>({
      query: ({ org, id, ...body }) => ({
        url: `email-templates/${id}/rewrite-tone`,
        method: `POST`,
        headers: {
          [orgHeader]: org,
        },
        data: body,
      }),
    }),
  }),
});

export const {
  useFetchEmailTemplatesQuery,
  useCreateEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
  useDuplicateEmailTemplateMutation,
  useSendTestEmailMutation,
  useCreateEmailTemplateChildMutation,
  useGenerateEmailTemplateChildrenMutation,
  useRewriteEmailTemplateToneMutation,
} = emailTemplatesApi;
